import { ErrorBoundary, For, Show } from 'solid-js';
import { customShadowlessElement, type CorrectComponentType } from '@brytdesigns/web-component-core/utils';
import { correctElementType } from '@brytdesigns/web-component-core';

import { useCartRecommendations } from '@/lib/cart-recommendations';
import { CompactCard, SkeletonCards, VerticalCard } from '@web-components/cart-recommendations-card';

type Props = {
  dataUrl: string;
  dataCartUrl: string;
  dataLimit: number;
  dataIntent: string;
  dataTitle: string;
  dataAutoshipLabel: string;
  dataSeparatorTop: boolean;
  dataCompact: boolean;
};

const Component: CorrectComponentType<Props> = (props, { element }) => {
  const limit = props.dataLimit || 6;
  const intent = props.dataIntent || 'related';

  const { recommendations, isLoading, hasItems } = useCartRecommendations({
    element,
    baseUrl: props.dataUrl,
    cartUrl: props.dataCartUrl,
    limit,
    intent,
  });

  const sliderId = `cart-recs-${Math.random().toString(36).slice(2, 8)}`;
  const sliderConfig = JSON.stringify({
    mode: 'free-snap',
    loop: false,
    slides: { perView: 'auto', spacing: 12 },
  });
  const applyConfig = (el: HTMLElement) => el.setAttribute('config', sliderConfig);

  return (
    <ErrorBoundary fallback={null}>
      <Show when={hasItems()}>
        <div classList={{ 'border-t border-foreground/10 py-4': props.dataSeparatorTop }}>
          <h3 class="text-2xl text-2xl--title font-bold text-heading mt-0 mb-3">{props.dataTitle}</h3>
          <Show when={!isLoading()} fallback={<SkeletonCards compact={props.dataCompact} />}>
            <keen-slider id={sliderId} ref={applyConfig} class="overflow-visible! keen-slider">
              <For each={recommendations()}>
                {(product) =>
                  props.dataCompact ? (
                    <CompactCard
                      product={product}
                      cartUrl={props.dataCartUrl}
                      autoshipLabel={props.dataAutoshipLabel}
                    />
                  ) : (
                    <VerticalCard
                      product={product}
                      cartUrl={props.dataCartUrl}
                      autoshipLabel={props.dataAutoshipLabel}
                    />
                  )
                }
              </For>
            </keen-slider>
          </Show>
        </div>
      </Show>
    </ErrorBoundary>
  );
};

customShadowlessElement(
  'cart-recommendations',
  {
    dataUrl: '',
    dataCartUrl: '/cart/add',
    dataLimit: 6,
    dataIntent: 'related',
    dataTitle: 'You may also like',
    dataAutoshipLabel: 'with Autoship',
    dataSeparatorTop: false,
    dataCompact: false,
  },
  correctElementType(Component),
);
